/** @typedef {{ id: number; x: number; z: number; kind: 'sidewalk' | 'intersection' }} NavNode */

/** @typedef {{ to: number; cost: number }} NavEdge */

import { getCityOffset, getCitySpan } from '../world/city/cityLayout.js';
import {
  blockStride,
  horizontalRoadCenterZ,
  verticalRoadCenterX,
} from '../world/streaming/worldGrid.js';

/**
 * Pedestrian navigation graph on sidewalks and intersections.
 */
export class NavigationGraph {
  constructor() {
    /** @type {NavNode[]} */
    this.nodes = [];
    /** @type {Map<number, NavEdge[]>} */
    this.edges = new Map();
    /** @type {Map<string, number>} */
    this._keyToId = new Map();
  }

  /**
   * @param {import('../config.js').CONFIG['city']} cityConfig
   * @param {number} [nodeSpacing]
   */
  static build(cityConfig, nodeSpacing = 5) {
    const graph = new NavigationGraph();
    graph._generateNodes(cityConfig, nodeSpacing);
    graph._connectNeighbors(6.5);
    return graph;
  }

  /**
   * Append nav nodes for a world region (streaming chunks). Uses global road grid (origin at 0).
   * @param {import('../config.js').CONFIG['city']} cityConfig
   * @param {{ minX: number; maxX: number; minZ: number; maxZ: number }} bounds
   * @param {number} [nodeSpacing]
   */
  appendRegion(cityConfig, bounds, nodeSpacing = 5) {
    this._generateNodesGlobal(cityConfig, bounds, nodeSpacing);
  }

  /** Rebuild edge links after merging chunk regions. */
  reconnectNeighbors(maxDistance = 6.5) {
    for (const id of this.edges.keys()) {
      this.edges.set(id, []);
    }
    this._connectNeighbors(maxDistance);
  }

  _generateNodesGlobal(config, bounds, spacing) {
    const pad = 6;
    const minX = bounds.minX - pad;
    const maxX = bounds.maxX + pad;
    const minZ = bounds.minZ - pad;
    const maxZ = bounds.maxZ + pad;
    const { roadWidth, sidewalkWidth } = config;
    const swOffset = roadWidth / 2 + sidewalkWidth / 2;
    const stride = blockStride(config);

    const iMin = Math.floor((minX - roadWidth / 2) / stride) - 1;
    const iMax = Math.ceil((maxX - roadWidth / 2) / stride) + 1;
    const jMin = Math.floor((minZ - roadWidth / 2) / stride) - 1;
    const jMax = Math.ceil((maxZ - roadWidth / 2) / stride) + 1;

    for (let i = iMin; i <= iMax; i += 1) {
      const roadX = verticalRoadCenterX(i, config);
      if (roadX + roadWidth / 2 < minX || roadX - roadWidth / 2 > maxX) continue;
      const westX = roadX - swOffset;
      const eastX = roadX + swOffset;
      for (let z = minZ + spacing * 0.5; z < maxZ; z += spacing) {
        this._addNode(westX, z, 'sidewalk');
        this._addNode(eastX, z, 'sidewalk');
      }
    }

    for (let j = jMin; j <= jMax; j += 1) {
      const roadZ = horizontalRoadCenterZ(j, config);
      if (roadZ + roadWidth / 2 < minZ || roadZ - roadWidth / 2 > maxZ) continue;
      const northZ = roadZ - swOffset;
      const southZ = roadZ + swOffset;
      for (let x = minX + spacing * 0.5; x < maxX; x += spacing) {
        this._addNode(x, northZ, 'sidewalk');
        this._addNode(x, southZ, 'sidewalk');
      }
    }

    for (let i = iMin; i <= iMax; i += 1) {
      for (let j = jMin; j <= jMax; j += 1) {
        const x = verticalRoadCenterX(i, config);
        const z = horizontalRoadCenterZ(j, config);
        if (x < minX - 1 || x > maxX + 1 || z < minZ - 1 || z > maxZ + 1) continue;
        this._addNode(x, z, 'intersection');
      }
    }
  }

  getNode(id) {
    return this.nodes[id];
  }

  /** @returns {number} */
  getRandomNodeId(rng) {
    return rng.int(0, this.nodes.length - 1);
  }

  _generateNodes(config, spacing) {
    const offset = getCityOffset(config);
    const span = getCitySpan(config);
    const { blockCount, roadWidth, sidewalkWidth } = config;
    const swOffset = roadWidth / 2 + sidewalkWidth / 2;

    for (let i = 0; i <= blockCount; i += 1) {
      const roadX =
        offset + i * (config.lotSize + config.roadWidth) + roadWidth / 2;
      const westX = roadX - swOffset;
      const eastX = roadX + swOffset;

      for (let z = offset + spacing * 0.5; z < offset + span; z += spacing) {
        this._addNode(westX, z, 'sidewalk');
        this._addNode(eastX, z, 'sidewalk');
      }
    }

    for (let j = 0; j <= blockCount; j += 1) {
      const roadZ =
        offset + j * (config.lotSize + config.roadWidth) + roadWidth / 2;
      const northZ = roadZ - swOffset;
      const southZ = roadZ + swOffset;

      for (let x = offset + spacing * 0.5; x < offset + span; x += spacing) {
        this._addNode(x, northZ, 'sidewalk');
        this._addNode(x, southZ, 'sidewalk');
      }
    }

    for (let i = 0; i <= blockCount; i += 1) {
      for (let j = 0; j <= blockCount; j += 1) {
        const x =
          offset + i * (config.lotSize + config.roadWidth) + roadWidth / 2;
        const z =
          offset + j * (config.lotSize + config.roadWidth) + roadWidth / 2;
        this._addNode(x, z, 'intersection');
      }
    }
  }

  _addNode(x, z, kind) {
    const key = `${Math.round(x * 4)}_${Math.round(z * 4)}`;
    if (this._keyToId.has(key)) {
      const id = this._keyToId.get(key);
      if (kind === 'intersection' && this.nodes[id].kind !== 'intersection') {
        this.nodes[id].kind = 'intersection';
      }
      return id;
    }
    const id = this.nodes.length;
    this.nodes.push({ id, x, z, kind });
    this._keyToId.set(key, id);
    this.edges.set(id, []);
    return id;
  }

  _connectNeighbors(maxDistance) {
    const cell = maxDistance;
    /** @type {Map<string, number[]>} */
    const buckets = new Map();

    for (const node of this.nodes) {
      const bx = Math.floor(node.x / cell);
      const bz = Math.floor(node.z / cell);
      const bkey = `${bx},${bz}`;
      if (!buckets.has(bkey)) buckets.set(bkey, []);
      buckets.get(bkey).push(node.id);
    }

    for (const node of this.nodes) {
      const bx = Math.floor(node.x / cell);
      const bz = Math.floor(node.z / cell);
      for (let dx = -1; dx <= 1; dx += 1) {
        for (let dz = -1; dz <= 1; dz += 1) {
          const list = buckets.get(`${bx + dx},${bz + dz}`);
          if (!list) continue;
          for (const otherId of list) {
            if (otherId <= node.id) continue;
            const other = this.nodes[otherId];
            const dist = Math.hypot(other.x - node.x, other.z - node.z);
            if (dist > maxDistance || dist < 0.01) continue;
            this._link(node.id, otherId, dist);
          }
        }
      }
    }
  }

  _link(a, b, cost) {
    this.edges.get(a).push({ to: b, cost });
    this.edges.get(b).push({ to: a, cost });
  }
}
